import React, { useEffect, useRef, useState } from "react";
import { BrowserRouter, Link, Route, Routes } from "react-router-dom";
import { routes } from "@/Config/routes";
import { AppContext } from "@/Components/Context/AppContext";
import { Toast } from 'primereact/toast';
import { showFlashMessages, url } from "@/Helpers/general";
import { createStorageObject, getStorageObject } from "@/Helpers/storage";
import { Button } from "primereact/button";
import * as Sentry from '@sentry/react';
import { useAuth0 } from "@auth0/auth0-react";
import ContentLoadingTemplate from "@/Components/Templates/ContentLoadingTemplate";
import { useAuthStore } from "@/Stores/AuthStore";
import {addLocale} from "primereact/api";

/**
 * Site's main routing page
 */
const App = () => {
    const toastRef = useRef();
    const toastBCRef = useRef();
    const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

    const storedAuthUser = useAuthStore((state) => state.authUser);
    const [authenticatedUserData, setAuthenticatedUserData] = useState(storedAuthUser);
    const [userPreferences, setUserPreferences] = useState(getStorageObject('preferences'));

    const { isAuthenticated, isLoading } = useAuth0();
    addLocale('en', {
        firstDayOfWeek: 1, // 1 = Monday, 0 = Sunday
        dayNames: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
        dayNamesShort: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
        monthNames: [
            'January', 'February', 'March', 'April', 'May', 'June',
            'July', 'August', 'September', 'October', 'November', 'December'
        ],
        monthNamesShort: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
        today: 'Today',
        clear: 'Clear'
    });

    useEffect(() => {
        showFlashMessages(toastRef);

        if (!localStorage.getItem('accepted_cookies')) {
            toastBCRef.current.show({
                severity: 'info',
                sticky: true,
                closable: false,
                content: (<>
                    <div>
                        This website uses cookies so that we can improve your experience. By continuing to use the site you are agreeing to our use of cookies. For more details please view our <Link className="p-link" to={url('cookies')}>Cookies Policy</Link>
                    </div>
                    <Button className="p-button-outlined bg-transparent p-button-secondary p-button-sm block mt-3" onClick={() => {
                        localStorage.setItem('accepted_cookies', true);
                        toastBCRef.current.clear();
                    }}>Accept and Close</Button>
                </>)
            });
        }
    }, []);

    useEffect(() => {
        createStorageObject('preferences', userPreferences);
    }, [userPreferences]);

    return (
        <BrowserRouter>
            {
                isLoading
                    ? <ContentLoadingTemplate/>
                    : <>
                        <AppContext.Provider value={{ toastRef, authenticatedUserData, setAuthenticatedUserData, userPreferences, setUserPreferences }}>
                            <Toast ref={toastRef} />

                            <SentryRoutes>
                                {routes[isAuthenticated ? 'private' : 'public'].map((route, key) => {
                                    return <Route key={key} path={route.path} exact element={route.component} />;
                                })}
                            </SentryRoutes>

                            <Toast ref={toastBCRef} className="cookies-notification" position="bottom-left" />
                        </AppContext.Provider>
                    </>
            }
        </BrowserRouter>
    );
};

export default App;
